import { Fragment } from 'react';
import { Writable } from 'stream';
import { Link, useActionData, useLoaderData } from '@remix-run/react';
import {
  json,
  NodeOnDiskFile,
  UploadHandler,
  unstable_composeUploadHandlers,
  writeReadableStreamToWritable,
  readableStreamToString,
  writeAsyncIterableToWritable,
  LinksFunction } from
'@remix-run/node';
import {
  CalendarIcon,
  LocationMarkerIcon,
  UsersIcon,
  ChevronLeftIcon } from
'@heroicons/react/solid';
import type {
  LoaderFunction,
  ActionFunction,
  MetaFunction } from
'@remix-run/node';
import {
  unstable_parseMultipartFormData,
  unstable_createMemoryUploadHandler } from
'@remix-run/node';
import {
  getJobListItems,
  getJob,
  applyForJob,
  getJobForView,
  saveApplicationSubmissionLog } from
'~/models/job.server';
import { ApplicationForm } from '~/components/ApplicationForm';
import { Transition } from '@headlessui/react';
import { groupBy, values, map, fromPairs, flow } from 'lodash/fp';
import { ATS, JobApplicationForm, Prisma } from '@prisma/client';

import { useOptionalUser } from '~/utils';
import type { Job } from '@prisma/client';
import { apply } from '~/ats/apply.server';
import type { ErrorsList, PageConfiguration } from '~/types';
import { getAccountById } from '~/models/account.server';
import { addEvent } from '~/models/event.server';
import { useSessionRewind } from '~/components/hooks/useSessionRewind';
import {
  generateApiToken,
  getJobForm,
  submitApplication } from
'~/services/api.server';
import { Question } from '~/types/apiTypes';
import { NewApplicationForm } from '~/components/NewApplicationForm';

type LoaderData = {
  job: Awaited<ReturnType<typeof getJobForView>>;
  applicationForm: {questions: Question[];};
  livePreviewUrl: string;
  includeSessionRewind: boolean;
};

type ActionData = {
  ok?: boolean;
  errors: ErrorsList;
};

export const action: ActionFunction = async ({ request, params }) => {
  // check if file uploaded: data.get('...').size
  const job = await getJob({ id: params.jobId! });

  if (!job || !job.applicationForm) {
    throw new Response('Not found', { status: 404 });
  }

  const uploadedFiles: {[key: string]: {name: string;size: number;};} = {};

  const uploadHandler = unstable_composeUploadHandlers(
    async ({ name, contentType, data, filename }) => {
      // if (job.ats === ATS.LEVER) {
      //   return undefined
      // }

      if (!contentType) {
        return undefined;
      }

      const chunks = [];

      for await (const chunk of data) {
        chunks.push(chunk);
      }

      if (chunks.length === 0) {
        // @FIXME refactor
        return '';
      }

      const b = Buffer.concat(chunks);

      uploadedFiles[name] = {
        name: filename!,
        size: b.byteLength
      };

      return b.toString('base64');
    },
    unstable_createMemoryUploadHandler()
  );

  const data = await unstable_parseMultipartFormData(request, uploadHandler);
  const formData = Object.fromEntries(data);

  // const uploadHandler = unstable_createMemoryUploadHandler({
  //   maxPartSize: 500_000,
  // });

  let result;
  let caughtError;

  if (job.ats === ATS.LEVER) {
    const dataToSubmit = {
      jobId: job.id,
      answers: [] as {questionId: string;value: any;}[]
    };

    const entries = Object.entries(formData);

    for (let [key, value] of entries) {
      let val: any = value;

      if (!val) {
        continue;
      }

      if (key in uploadedFiles) {
        val = {
          content: data.get(key),
          fileName: uploadedFiles[key].name
        };
      }

      dataToSubmit.answers.push({
        questionId: key,
        value: val
      });
    }

    console.log('dataToSubmit', dataToSubmit.answers);

    let ce;
    let response;

    try {
      response = await submitApplication(dataToSubmit);
    } catch (error: any) {
      ce = error;
    }

    await saveApplicationSubmissionLog({
      job,
      data: { formData, uploadedFiles, result },
      errors: {
        message: ce?.message,
        responseMaybe: ce?.response?.data
      }
    });

    if (!response) {
      return { ok: false };
    }

    return { ok: response.ok, errors: response.errors ?? undefined };
  }

  try {
    result = await apply({
      job: job as Job & {applicationForm: JobApplicationForm;},
      formData,
      rawFormData: data,
      uploadedFiles
    });
  } catch (error: any) {
    caughtError = error;
    // console.error(error)
  }

  await saveApplicationSubmissionLog({
    job,
    data: { formData, uploadedFiles, result },
    errors: {
      message: caughtError?.message,
      responseMaybe: caughtError?.response?.data
    }
  });

  if (!result?.ok) {
    return {
      ok: false,
      errors: result?.errors
    };
  }

  return { ok: true };
};

export const meta: MetaFunction = ({ data }) => {
  const { job, livePreviewUrl } = data as LoaderData;
  const pageConfiguration = job?.account?.careerPage?.
  pageConfiguration as PageConfiguration;

  const meta = pageConfiguration.props.meta;

  const titleArray = ['Apply for ', job?.name];

  if (meta?.title) {
    titleArray.push(` - ${meta.title}`);
  }

  const title = titleArray.join('');
  const description = meta?.description ?? '';

  const extraTags = [];

  if (job?.departments && job?.departments.length > 0) {
    extraTags.push({ label: 'Department', data: job?.departments[0] });
  }

  if (job?.locations && job?.locations?.length > 0) {
    extraTags.push({ label: 'Location', data: job?.locations[0] });
  }

  const extra = extraTags.reduce((acc, tag, index) => {
    return {
      ...acc,
      [`twitter:label${index + 1}`]: tag.label,
      [`twitter:data${index + 1}`]: tag.data
    };
  }, {});

  return {
    // title: job?.name,
    title,
    description,
    'og:type': 'website',
    'og:url': `${livePreviewUrl}/jobs/${job?.id}`,
    'og:title': title,
    'og:description': description,
    'og:image':
    meta?.ogImageUrl?.replace(
      'https://s3.us-east-2.amazonaws.com/growhirestaging/',
      'https://d1icxrsvmhiz31.cloudfront.net/'
    ) ?? undefined,
    'twitter:card': meta?.ogImageUrl ? 'summary_large_image' : 'summary',
    'twitter:url': `${livePreviewUrl}/jobs/${job?.id}`,
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image':
    meta?.ogImageUrl?.replace(
      'https://s3.us-east-2.amazonaws.com/growhirestaging/',
      'https://d1icxrsvmhiz31.cloudfront.net/'
    ) ?? undefined,
    ...extra
  };
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const id = params.jobId;
  if (!id) {
    throw new Response('Not Found', { status: 404 });
  }

  const job = await getJobForView({ id });

  if (!job) {
    throw new Response('Not Found', { status: 404 });
  }

  const cp = job?.account?.careerPage;

  let livePreviewUrl =
  process.env.NODE_ENV === 'development' ?
  `http://localhost:3000?slug=${cp?.slug}` :
  `https://${cp?.slug}.${process.env.DOMAIN_NAME_FOR_PREVIEW}`;

  if (cp?.customDomain) {
    livePreviewUrl = `https://${cp.customDomain}`;
  }

  let applicationForm;
  if (job.ats === ATS.LEVER) {
    applicationForm = await getJobForm(job);
  }

  // try {
  //   if (job.accountId) {
  //     const account = await getAccountById({ id: job.accountId })
  //     if (account) {
  //       await addEvent({ name: 'page.visit', account, payload: { page: 'apply', url: request.url, ip: context.ip } })
  //     }
  //   }
  // } catch (e) {
  //   console.error(e)
  // }

  // @REFACTOR @FIXME
  const jobMeta = job.meta as Prisma.JsonObject | null;

  if (jobMeta?.html) {
    delete jobMeta.html;
  }

  const includeSessionRewind = process.env.NODE_ENV === 'production';

  return json<{
    job: Awaited<ReturnType<typeof getJobForView>>;
    applicationForm: {questions: Question[];};
    livePreviewUrl: string;
    includeSessionRewind: boolean;
  }>({ job, applicationForm, livePreviewUrl, includeSessionRewind });
};

export const links: LinksFunction = () => {
  return [
  {
    rel: 'preload',
    as: 'style',
    href: '/style.css'
  },
  {
    rel: 'stylesheet',
    href: '/style.css'
  },
  {
    rel: 'icon',
    href: '/favicon.png'
  }];

};

export default function JobPage() {
  const data = useLoaderData<LoaderData>();
  const actionData = useActionData() as ActionData;
  const job = data.job;

  useSessionRewind({ enabled: data.includeSessionRewind });

  if (!job) {
    return null;
  }

  const isSuccess = actionData?.ok;

  return (
    <>
      <div className="relative overflow-hidden bg-white py-16">
        <div className="hidden lg:absolute lg:inset-y-0 lg:block lg:h-full lg:w-full">
          <div
            className="relative mx-auto h-full max-w-prose text-lg"
            aria-hidden="true">
            
            <svg
              className="absolute left-full top-12 translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384">
              
              <defs>
                <pattern
                  id="74b3fd99-0a6f-4271-bef2-e80eeafdf357"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse">
                  
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor" />
                  
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)" />
              
            </svg>
            <svg
              className="absolute right-full top-1/2 -translate-x-32 -translate-y-1/2 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384">
              
              <defs>
                <pattern
                  id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse">
                  
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor" />
                  
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)" />
              
            </svg>
            <svg
              className="absolute bottom-12 left-full translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384">
              
              <defs>
                <pattern
                  id="d3eb07ae-5182-43e6-857d-35c643af9034"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse">
                  
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor" />
                  
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#d3eb07ae-5182-43e6-857d-35c643af9034)" />
              
            </svg>
          </div>
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-prose text-lg">
            <div className="mb-12">
              <Link
                to={`/jobs/${job.id}`}
                className="flex items-center space-x-1 text-sm font-medium text-accent-color">
                
                <ChevronLeftIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                <span>Job description</span>
              </Link>
            </div>
            <h1>
              <span className="block text-center text-base font-semibold uppercase tracking-wide text-accent-color">
                Full time
              </span>
              <span className="mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                {job.name}
              </span>
            </h1>
            <p className="text-md mb-8 mt-4 flex items-center justify-center leading-8 text-gray-500">
              <span>{job.locations.join(', ')}</span>
              <svg
                className="mx-1.5 h-2 w-2 text-accent-color"
                fill="currentColor"
                viewBox="0 0 8 8">
                
                <circle cx={4} cy={4} r={3} />
              </svg>
              <span>{job.departments[0]}</span>
            </p>
            {/* <p className="mt-8 text-xl text-gray-500 leading-8">
              Aliquet nec orci mattis amet quisque ullamcorper neque, nibh sem. At arcu, sit dui mi, nibh dui, diam eget
              aliquam. Quisque id at vitae feugiat egestas ac. Diam nulla orci at in viverra scelerisque eget. Eleifend
              egestas fringilla sapien.
              </p> */}
          </div>
          <div className="mx-auto max-w-3xl">
            {isSuccess &&
            <div>
                <span className="relative block text-center text-6xl">🙌</span>
                <p className="text-md mt-8 text-center font-normal text-gray-600">
                  Thank you for submitting your application. We'll contact you
                  within 24 hours.
                </p>
              </div>
            }
            {(!isSuccess || process.env.NODE_ENV === 'development') &&
            job.ats !== ATS.LEVER &&
            <ApplicationForm job={job} errors={actionData?.errors} />
            }
            {(!isSuccess || process.env.NODE_ENV === 'development') &&
            job.ats === ATS.LEVER &&
            <NewApplicationForm
              job={job}
              formDefinition={data.applicationForm}
              errors={actionData?.errors} />

            }
          </div>
        </div>
      </div>
      {/* <Example /> */}
    </>);

}